import { useEffect, useState } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { useServers, useBootstrapStatus } from "@/lib/hooks";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CodeBlock } from "@/components/CodeBlock";
import {
  Server, Plus, RefreshCcw, Zap, Trash2, CheckCircle2, XCircle,
  Loader2, ShieldCheck, TerminalSquare, CornerDownLeft, Pencil,
  Cloud, ExternalLink, Copy,
} from "lucide-react";
import { toast } from "sonner";

function Pill({ ok, label }) {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border"
      style={{
        background: ok ? "hsl(173 80% 20%)" : "hsl(222 18% 18%)",
        color: ok ? "hsl(166 76% 92%)" : "hsl(215 20% 70%)",
        borderColor: "transparent",
      }}>
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
      {label}
    </span>
  );
}

export default function ServersPage() {
  const { data: servers, mutate } = useServers();
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Servers</h1>
          <p className="text-muted-foreground mt-1">
            Register your VPS(s). The panel connects over SSH to install Docker + Caddy and deploy your sites.
          </p>
        </div>
        <Dialog open={creating} onOpenChange={setCreating}>
          <DialogTrigger asChild>
            <Button data-testid="servers-add-button" className="gap-2">
              <Plus className="h-4 w-4" /> Add server
            </Button>
          </DialogTrigger>
          <AddServerDialog
            onClose={() => setCreating(false)}
            onDone={() => { setCreating(false); mutate(); }}
          />
        </Dialog>
      </header>

      {(!servers || servers.length === 0) && (
        <Card className="p-10 text-center border-border">
          <div className="mx-auto h-12 w-12 rounded-lg surface-2 grid place-items-center mb-3">
            <Server className="h-6 w-6" />
          </div>
          <div className="font-medium">No servers yet</div>
          <div className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Add your VPS SSH details. emhost will bootstrap Docker + Caddy on it and use it as a deploy target.
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(servers || []).map((s) => (
          <ServerCard key={s.id} server={s} onChanged={mutate} />
        ))}
      </div>
    </div>
  );
}

function ServerCard({ server, onChanged }) {
  const [testing, setTesting] = useState(false);
  const [bootstrapOpen, setBootstrapOpen] = useState(false);

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/servers/${server.id}/test`);
      if (data.reachable) toast.success("Server reachable");
      else toast.error(data.error || "Not reachable");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const del = async () => {
    try {
      await api.delete(`/servers/${server.id}`);
      toast.success("Server removed");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Card className="p-5 border-border" data-testid={`server-card-${server.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Server className="h-4 w-4 text-primary" />
            <div className="font-medium truncate">{server.name}</div>
            {server.provider && (
              <span className="text-[11px] px-1.5 py-0.5 rounded surface-2 text-muted-foreground">
                {server.provider}
              </span>
            )}
          </div>
          <div className="font-mono text-[12.5px] text-muted-foreground mt-1 truncate">
            {server.username}@{server.host}:{server.port}
          </div>
        </div>
        <Pill ok={server.reachable} label={server.reachable ? "reachable" : "unreachable"} />
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        <Pill ok={server.docker} label="docker" />
        <Pill ok={server.docker_compose} label="compose" />
        <Pill ok={server.caddy} label="caddy" />
        <Pill ok={server.webnet} label="webnet" />
      </div>

      {server.password_decryptable === false && (
        <div
          data-testid={`server-pw-undecryptable-${server.id}`}
          className="mt-3 text-[12px] rounded p-2 border"
          style={{ background: "hsl(35 92% 12%)", borderColor: "hsl(35 92% 30%)", color: "hsl(33 100% 90%)" }}
        >
          <div className="font-medium">Stored password cannot be decrypted.</div>
          <div className="mt-0.5 opacity-90">
            The encryption key changed since this password was saved (typically after a panel redeploy).
            Click <b>Edit</b> below and re-enter the SSH password, then Test.
          </div>
        </div>
      )}

      {server.last_error && (
        <div className="mt-3 text-[12px] text-red-300/90 bg-red-950/40 border border-red-900/50 rounded p-2 font-mono truncate">
          {server.last_error}
        </div>
      )}
      {server.os && !server.last_error && (
        <div className="mt-3 text-[12px] text-muted-foreground font-mono truncate">{server.os}</div>
      )}

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="secondary" className="gap-1.5"
                onClick={test} disabled={testing}
                data-testid={`server-test-${server.id}`}>
          {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
          Test
        </Button>
        <Dialog open={bootstrapOpen} onOpenChange={setBootstrapOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5" data-testid={`server-bootstrap-${server.id}`}>
              <Zap className="h-3.5 w-3.5" /> Bootstrap
            </Button>
          </DialogTrigger>
          <BootstrapDialog
            server={server}
            onClose={() => setBootstrapOpen(false)}
            onDone={() => onChanged?.()}
          />
        </Dialog>

        <Dialog>
          <DialogTrigger asChild>
            <Button size="sm" variant="secondary" className="gap-1.5"
                    data-testid={`server-shell-${server.id}`}>
              <TerminalSquare className="h-3.5 w-3.5" /> Shell
            </Button>
          </DialogTrigger>
          <ServerShellDialog server={server} />
        </Dialog>

        <CloudflareTunnelButton server={server} onChanged={onChanged} />

        <EditServerButton server={server} onChanged={onChanged} />

        <div className="ml-auto">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="icon" className="text-muted-foreground">
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove this server?</AlertDialogTitle>
                <AlertDialogDescription>
                  Sites currently targeting <code>{server.name}</code> will fall back to simulation mode. This does not touch anything on the VPS itself.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={del}>Remove</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </Card>
  );
}

function AddServerDialog({ onClose, onDone }) {
  const [f, setF] = useState({
    name: "", host: "", port: 22, username: "root", password: "", provider: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.name || !f.host || !f.username || !f.password) {
      toast.error("Name, host, username and password are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/servers", { ...f, port: Number(f.port) || 22 });
      toast.success("Server added");
      // immediately test
      try { await api.post(`/servers/${data.id}/test`); } catch { /* ignore */ }
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add server");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>Add server</DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 space-y-1.5">
          <Label>Name</Label>
          <Input data-testid="add-server-name" placeholder="My DatabaseMart VPS" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </div>
        <div className="col-span-2 space-y-1.5">
          <Label>Host / IP</Label>
          <Input data-testid="add-server-host" className="font-mono" placeholder="93.127.133.193" value={f.host} onChange={(e) => setF({ ...f, host: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>SSH port</Label>
          <Input data-testid="add-server-port" className="font-mono" placeholder="22" value={f.port} onChange={(e) => setF({ ...f, port: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>Provider (optional)</Label>
          <Input placeholder="DatabaseMart" value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>Username</Label>
          <Input data-testid="add-server-username" className="font-mono" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>Password</Label>
          <Input data-testid="add-server-password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
        <ShieldCheck className="h-3.5 w-3.5" /> Password is encrypted at rest (Fernet). It&apos;s used only to open short-lived SSH connections.
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={busy} data-testid="add-server-save">
          {busy ? "Adding…" : "Add & test"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function BootstrapDialog({ server, onClose, onDone }) {
  const [started, setStarted] = useState(false);
  const { data: st } = useBootstrapStatus(started ? server.id : null);

  const startBootstrap = async () => {
    try {
      await api.post(`/servers/${server.id}/bootstrap`);
      setStarted(true);
      toast.info("Bootstrap started — this can take ~2 minutes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start bootstrap");
    }
  };

  useEffect(() => {
    if (st?.status === "done") { toast.success("Bootstrap complete"); onDone?.(); }
    if (st?.status === "failed") { toast.error("Bootstrap failed — see logs below"); onDone?.(); }
  }, [st?.status]);

  const status = st?.status || "idle";
  const log = (st?.log || []).join("\n");

  return (
    <DialogContent className="sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <TerminalSquare className="h-4 w-4" /> Bootstrap {server.name}
        </DialogTitle>
      </DialogHeader>
      <div className="text-sm text-muted-foreground">
        This will SSH into <code className="font-mono">{server.username}@{server.host}:{server.port}</code> and install:
        <ul className="mt-2 ml-4 list-disc space-y-0.5">
          <li>Docker + docker compose (v2 plugin)</li>
          <li>Caddy (with automatic HTTPS)</li>
          <li>A shared <code className="font-mono">webnet</code> Docker network</li>
          <li><code className="font-mono">/opt/emhost/sites/</code> and <code className="font-mono">/etc/caddy/emhost/</code> directories</li>
        </ul>
      </div>

      {!started && (
        <div className="flex items-center justify-between mt-2">
          <div className="text-xs text-muted-foreground">Requires that the SSH user can <code>sudo</code>.</div>
          <Button onClick={startBootstrap} className="gap-2" data-testid="bootstrap-start-button">
            <Zap className="h-4 w-4" /> Start bootstrap
          </Button>
        </div>
      )}

      {started && (
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            {status === "running" && <><Loader2 className="h-4 w-4 animate-spin" /> Installing…</>}
            {status === "done"    && <><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Complete</>}
            {status === "failed"  && <><XCircle className="h-4 w-4 text-red-400" /> Failed</>}
          </div>
          <CodeBlock label="bootstrap.log" language="log" code={log || "(waiting for output)"} maxHeight="320px" />
        </div>
      )}

      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function EditServerButton({ server, onChanged }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(server.name || "");
  const [host, setHost] = useState(server.host || "");
  const [port, setPort] = useState(String(server.port || 22));
  const [username, setUsername] = useState(server.username || "");
  const [password, setPassword] = useState("");
  const [notes, setNotes] = useState(server.notes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(server.name || "");
      setHost(server.host || "");
      setPort(String(server.port || 22));
      setUsername(server.username || "");
      setPassword("");
      setNotes(server.notes || "");
    }
  }, [open, server]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { name, host, port: Number(port), username, notes };
      if (password) body.password = password;
      await api.patch(`/servers/${server.id}`, body);
      toast.success(password ? "Server updated (password re-encrypted)" : "Server updated");
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "update failed");
    } finally {
      setSaving(false);
    }
  };

  const needsPw = server.password_decryptable === false;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant={needsPw ? "default" : "secondary"}
          className="gap-1.5"
          data-testid={`server-edit-${server.id}`}
          style={needsPw ? { background: "hsl(43 96% 45%)", color: "hsl(30 40% 12%)" } : undefined}
        >
          <Pencil className="h-3.5 w-3.5" />
          {needsPw ? "Re-enter password" : "Edit"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit server — {server.name}</DialogTitle>
        </DialogHeader>
        {needsPw && (
          <div className="text-sm rounded p-3 border"
               style={{ background: "hsl(35 92% 12%)", borderColor: "hsl(35 92% 30%)", color: "hsl(33 100% 90%)" }}>
            The stored password can no longer be decrypted (encryption key changed after a panel redeploy).
            <b> Please re-enter the SSH password below</b> — the rest of the fields can be left as-is.
          </div>
        )}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                     data-testid={`server-edit-name-${server.id}`} />
            </div>
            <div>
              <Label>Host</Label>
              <Input value={host} onChange={(e) => setHost(e.target.value)}
                     data-testid={`server-edit-host-${server.id}`} />
            </div>
            <div>
              <Label>Port</Label>
              <Input value={port} onChange={(e) => setPort(e.target.value)}
                     data-testid={`server-edit-port-${server.id}`} />
            </div>
            <div>
              <Label>Username</Label>
              <Input value={username} onChange={(e) => setUsername(e.target.value)}
                     data-testid={`server-edit-username-${server.id}`} />
            </div>
          </div>
          <div>
            <Label>SSH password {needsPw ? <span className="text-amber-500">(required)</span> : <span className="text-muted-foreground text-xs">(leave blank to keep existing)</span>}</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                   placeholder="••••••••"
                   data-testid={`server-edit-password-${server.id}`} />
          </div>
          <div>
            <Label>Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                   data-testid={`server-edit-notes-${server.id}`} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || (needsPw && !password)}
                  data-testid={`server-edit-save-${server.id}`}
                  className="gap-1.5">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pencil className="h-3.5 w-3.5" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ServerShellDialog({ server }) {
  const [cmd, setCmd] = useState("");
  const [sudo, setSudo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [cmdHistory, setCmdHistory] = useState([]);
  const [histIdx, setHistIdx] = useState(-1);

  const run = async (e) => {
    e?.preventDefault?.();
    const c = cmd.trim();
    if (!c) return;
    setBusy(true);
    setCmdHistory((h) => (h[h.length - 1] === c ? h : [...h, c]));
    setHistIdx(-1);
    try {
      const { data } = await api.post(`/servers/${server.id}/exec`, { cmd: c, sudo });
      setHistory((h) => [...h, { ...data, ts: Date.now() }]);
      setCmd("");
    } catch (err) {
      setHistory((h) => [...h, {
        cmd: c, exit_code: -1, stdout: "", stderr: err?.response?.data?.detail || "failed",
        duration_ms: 0, ts: Date.now(),
      }]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (cmdHistory.length === 0) return;
      const ni = histIdx < 0 ? cmdHistory.length - 1 : Math.max(0, histIdx - 1);
      setHistIdx(ni); setCmd(cmdHistory[ni]);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (histIdx < 0) return;
      const ni = histIdx + 1;
      if (ni >= cmdHistory.length) { setHistIdx(-1); setCmd(""); }
      else { setHistIdx(ni); setCmd(cmdHistory[ni]); }
    }
  };

  const quickCmds = [
    "docker ps -a",
    "systemctl status caddy --no-pager -n 20",
    "journalctl -u caddy --no-pager -n 40",
    "ss -ltnp | grep -E ':(80|443)'",
    "df -h",
    "ls /etc/caddy/emhost",
  ];

  return (
    <DialogContent className="sm:max-w-3xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <TerminalSquare className="h-4 w-4" /> Host shell — {server.name}
        </DialogTitle>
      </DialogHeader>
      <div className="text-xs text-muted-foreground">
        Runs commands on the VPS host over SSH. Use this to check <code>docker ps</code>,
        Caddy status, ports, etc. Add <b>sudo</b> for system commands.
      </div>

      <div className="flex flex-wrap gap-1.5">
        {quickCmds.map((q) => (
          <Button key={q} variant="secondary" size="sm"
                  className="h-7 text-[11px] font-mono"
                  onClick={() => setCmd(q)}>
            {q}
          </Button>
        ))}
      </div>

      <div
        className="scroll-thin overflow-auto p-4 font-mono text-[12.5px] leading-6 whitespace-pre code-surface rounded-lg"
        style={{ height: 320 }}
        data-testid="server-shell-output"
      >
        {history.length === 0 && (
          <div className="text-muted-foreground">
            Ready. Type a command and press Enter. Try one of the quick commands above.
          </div>
        )}
        {history.map((h, i) => (
          <div key={i}>
            <div className="text-primary">{h.sudo ? "# " : "$ "}{h.cmd}</div>
            {h.stdout && <div>{h.stdout}</div>}
            {h.stderr && <div className="text-red-400">{h.stderr}</div>}
            <div className="text-[10.5px] text-muted-foreground">
              exit={h.exit_code} · {h.duration_ms}ms
            </div>
            <div className="mt-2" />
          </div>
        ))}
      </div>

      <form onSubmit={run} className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground flex items-center gap-1.5">
          <input type="checkbox" checked={sudo} onChange={(e) => setSudo(e.target.checked)} /> sudo
        </label>
        <Input
          value={cmd} onChange={(e) => setCmd(e.target.value)} onKeyDown={onKey}
          placeholder="docker ps"
          className="font-mono h-9"
          data-testid="server-shell-input"
          disabled={busy}
          autoFocus
        />
        <Button type="submit" size="sm" className="gap-1.5"
                data-testid="server-shell-run"
                disabled={busy || !cmd.trim()}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CornerDownLeft className="h-3.5 w-3.5" />}
          Run
        </Button>
      </form>
    </DialogContent>
  );
}


function CloudflareTunnelButton({ server, onChanged }) {
  const [open, setOpen] = useState(false);
  const { data: status, mutate: refreshStatus, isLoading } = useSWR(
    open ? `/servers/${server.id}/cloudflared-status` : null,
    async (url) => (await api.get(url)).data,
    { revalidateOnFocus: false }
  );
  const [step, setStep] = useState("idle"); // idle | installing | login | authorized | finalizing | done
  const [loginUrl, setLoginUrl] = useState("");
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      // reset when closed
      setStep("idle");
      setLoginUrl("");
      setLog([]);
    }
  }, [open]);

  const appendLog = (lines) => {
    if (!lines) return;
    setLog((prev) => [...prev, ...(Array.isArray(lines) ? lines : [lines])]);
  };

  const doInstall = async () => {
    setBusy(true);
    setStep("installing");
    appendLog(["$ install cloudflared on server (this can take ~1 min the first time)"]);
    try {
      const { data } = await api.post(`/servers/${server.id}/cloudflared-install`);
      appendLog(data.log || []);
      if (!data.ok) {
        toast.error(data.error || "install failed");
        setStep("idle");
        return;
      }
      appendLog([`[ok] cloudflared installed: ${data.version || "?"}`]);
      // move on to login
      await doStartLogin();
    } catch (e) {
      appendLog([`[!] ${e?.response?.data?.detail || e.message}`]);
      toast.error(e?.response?.data?.detail || "install failed");
      setStep("idle");
    } finally {
      setBusy(false);
    }
  };

  const doReset = async () => {
    setBusy(true);
    setStep("resetting");
    setLog([]);
    appendLog(["$ nuclear reset: wiping cloudflared state on VPS…"]);
    try {
      const { data } = await api.post(`/servers/${server.id}/cloudflared-reset`);
      appendLog(data.log || []);
      if (!data.ok) {
        appendLog([`[!] ${data.error || "reset failed"}`]);
        toast.error(data.error || "reset failed");
        setStep("idle");
        return;
      }
      appendLog(["[ok] reset complete — starting fresh install + login"]);
      refreshStatus();
      // Chain into install
      await doInstall();
    } catch (e) {
      appendLog([`[!] ${e?.response?.data?.detail || e.message}`]);
      toast.error(e?.response?.data?.detail || "reset failed");
      setStep("idle");
    } finally {
      setBusy(false);
    }
  };

  const doStartLogin = async () => {
    setBusy(true);
    setStep("login");
    appendLog(["$ cloudflared tunnel login (starting…)"]);
    try {
      const { data } = await api.post(`/servers/${server.id}/cloudflared-login`);
      if (!data.ok || !data.url) {
        appendLog([`[!] ${data.error || "no url returned"}`]);
        toast.error(data.error || "login failed to start");
        setStep("idle");
        return;
      }
      setLoginUrl(data.url);
      appendLog([`login URL: ${data.url}`,
                 "→ open this URL in your browser, sign in to Cloudflare, and pick the zone(s) you want this tunnel to route (include libercracker.xyz!)."]);
    } catch (e) {
      appendLog([`[!] ${e?.response?.data?.detail || e.message}`]);
      toast.error(e?.response?.data?.detail || "login failed");
      setStep("idle");
    } finally {
      setBusy(false);
    }
  };

  const doFinalize = async () => {
    setBusy(true);
    setStep("finalizing");
    appendLog(["", "$ finalize: create tunnel + write /etc/cloudflared/config.yml + start service"]);
    try {
      const { data } = await api.post(`/servers/${server.id}/cloudflared-finalize`, {
        tunnel_name: "emhost",
      });
      appendLog(data.log || []);
      if (!data.ok) {
        if (data.needs_login) {
          appendLog(["[!] cert.pem not found yet — did you complete the browser authorization?"]);
          toast.error("Complete the browser login first, then click Finalize again.");
          setStep("login");
          return;
        }
        appendLog([`[!] ${data.error || "finalize failed"}`]);
        if (data.error_detail) {
          appendLog(["", "─── journalctl -u cloudflared (last 40 lines) ───",
                     ...String(data.error_detail).split("\n").slice(-40)]);
        }
        toast.error(data.error || "finalize failed");
        setStep("login");
        return;
      }
      appendLog([`[ok] tunnel '${data.tunnel_name}' active (id=${data.tunnel_id})`,
                 "You can now attach custom domains whose zone you authorized during login."]);
      setStep("done");
      toast.success("Cloudflare Tunnel is live");
      refreshStatus();
      onChanged?.();
    } catch (e) {
      appendLog([`[!] ${e?.response?.data?.detail || e.message}`]);
      toast.error(e?.response?.data?.detail || "finalize failed");
      setStep("login");
    } finally {
      setBusy(false);
    }
  };

  const isActive = status?.active && status?.tunnel_name;
  const hasCert = status?.cert_present;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="secondary" className="gap-1.5"
                data-testid={`server-cf-tunnel-${server.id}`}>
          <Cloud className="h-3.5 w-3.5" />
          {isActive ? "CF Tunnel" : "Setup CF Tunnel"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5" />
            Cloudflare Tunnel — {server.name}
          </DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking status…
          </div>
        )}

        {!isLoading && status && (
          <div className="rounded-md border border-border p-3 space-y-1.5 text-sm"
               style={{ background: "hsl(210 40% 10%)" }}>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground w-32">Installed:</span>
              {status.installed
                ? <span style={{ color: "hsl(142 71% 78%)" }}>yes</span>
                : <span style={{ color: "hsl(0 90% 78%)" }}>no</span>}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground w-32">cert.pem present:</span>
              {status.cert_present
                ? <span style={{ color: "hsl(142 71% 78%)" }}>yes (browser login done)</span>
                : <span style={{ color: "hsl(35 92% 78%)" }}>no (browser login required)</span>}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground w-32">Tunnel active:</span>
              {isActive
                ? <span style={{ color: "hsl(142 71% 78%)" }}>{status.tunnel_name} ✓</span>
                : <span style={{ color: "hsl(35 92% 78%)" }}>none</span>}
            </div>
          </div>
        )}

        {!isActive && (
          <div className="space-y-3">
            <div className="text-sm rounded-md p-3 border"
                 style={{ background: "hsl(35 92% 12%)", borderColor: "hsl(35 92% 30%)",
                          color: "hsl(33 100% 88%)" }}>
              <div className="font-medium mb-1">3-step guided setup</div>
              <ol className="text-xs space-y-0.5 list-decimal ml-4">
                <li><b>Install</b> cloudflared on the VPS (if missing)</li>
                <li>Complete the <b>browser login</b> and pick which Cloudflare zones the tunnel may route (include <code>libercracker.xyz</code>!)</li>
                <li>Click <b>Finalize</b> — creates the tunnel + config + starts the systemd service</li>
              </ol>
            </div>

            {step === "idle" && (
              <div className="space-y-2">
                <Button
                  onClick={status?.installed ? doStartLogin : doInstall}
                  disabled={busy}
                  className="gap-1.5 w-full"
                  data-testid={`server-cf-tunnel-start-${server.id}`}
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                  {status?.installed ? "Start browser login" : "Install cloudflared + start login"}
                </Button>
                <Button
                  onClick={doReset}
                  disabled={busy}
                  variant="secondary"
                  className="gap-1.5 w-full"
                  data-testid={`server-cf-tunnel-reset-${server.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Reset &amp; Setup from scratch
                </Button>
                <div className="text-[11px] text-muted-foreground text-center">
                  Use Reset if a previous attempt got stuck (broken cert.pem,
                  half-installed service). Wipes tunnel state, then starts
                  fresh install + login.
                </div>
              </div>
            )}

            {step === "login" && loginUrl && (
              <div className="space-y-2">
                <Label>Open this URL in your browser, sign in, pick zones (include your custom-domain zone!), and authorize.</Label>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly value={loginUrl}
                    className="font-mono text-[11px]"
                    onClick={(e) => e.target.select()}
                    data-testid={`server-cf-tunnel-url-${server.id}`}
                  />
                  <Button
                    size="icon" variant="secondary"
                    onClick={() => { navigator.clipboard?.writeText(loginUrl); toast.success("URL copied"); }}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="icon" variant="secondary" asChild
                  >
                    <a href={loginUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </Button>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => refreshStatus()}
                    className="gap-1.5"
                  >
                    <RefreshCcw className="h-3.5 w-3.5" />
                    Recheck cert.pem
                  </Button>
                  <div className="text-[11px] text-muted-foreground">
                    {hasCert ? "cert detected — ready to finalize" : "waiting for browser authorization…"}
                  </div>
                </div>
                <Button
                  onClick={doFinalize}
                  disabled={busy}
                  className="gap-1.5 w-full mt-2"
                  data-testid={`server-cf-tunnel-finalize-${server.id}`}
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                  I&apos;ve authorized — Finalize setup
                </Button>
              </div>
            )}

            {step === "installing" || step === "finalizing" || step === "resetting" ? (
              <div className="text-sm flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {step === "installing" ? "Installing cloudflared…" :
                 step === "resetting" ? "Wiping cloudflared state…" :
                 "Finalizing tunnel setup…"}
              </div>
            ) : null}
          </div>
        )}

        {isActive && step !== "finalizing" && (
          <div className="text-sm rounded-md p-3 border"
               style={{ background: "hsl(142 40% 10%)", borderColor: "hsl(142 40% 30%)",
                        color: "hsl(142 71% 88%)" }}>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                Tunnel is active. You can now attach custom domains via each site&apos;s
                Custom Domain card. If a domain fails to attach because its zone isn&apos;t
                authorized, use <b>Re-authorize zones</b> below to add more zones to
                this tunnel&apos;s certificate.
              </div>
            </div>
            <div className="mt-2 flex gap-2 flex-wrap">
              <Button
                size="sm" variant="secondary" onClick={doStartLogin} disabled={busy}
                data-testid={`server-cf-tunnel-reauth-${server.id}`}
              >
                Re-authorize zones
              </Button>
              <Button
                size="sm" variant="secondary" onClick={doReset} disabled={busy}
                className="gap-1.5"
                data-testid={`server-cf-tunnel-reset-active-${server.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Reset &amp; rebuild
              </Button>
            </div>
          </div>
        )}

        {log.length > 0 && (
          <CodeBlock
            label="setup log"
            code={log.join("\n")}
            testid={`server-cf-tunnel-log-${server.id}`}
          />
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
